/**
 * Issue Report Service
 *
 * Frontend API client for submitting issue reports to the backend.
 */
import apiClient from './apiClient';
import { API_URL } from '../config';
import type { EvidenceBundle } from './evidenceCollectorService';

export type IssueCategory =
  | 'transcription'
  | 'cpt_selection'
  | 'report_generation'
  | 'intelligence_analytics'
  | 'ui_ux'
  | 'other';

export type IssueSeverity = 'critical' | 'high' | 'medium' | 'low';

export interface IssueReportPayload {
  category: IssueCategory;
  severity: IssueSeverity;
  description: string;
  evidence?: EvidenceBundle;
  screenshot?: string;
  screenshotMimeType?: string;
  url?: string;
  userAgent?: string;
  sessionId?: string;
  correlationId?: string;
}

export interface IssueReportApiResponse {
  id: number;
  category: string;
  severity: string;
  status: string;
  description: string;
  url?: string;
  session_id?: string;
  correlation_id?: string;
  github_issue_number?: number;
  github_issue_url?: string;
  created_at: string;
  updated_at?: string;
}

export interface IssueReportResponse {
  id: number;
  category: string;
  severity: string;
  status: string;
  description: string;
  url?: string;
  sessionId?: string;
  correlationId?: string;
  githubIssueNumber?: number;
  githubIssueUrl?: string;
  createdAt: string;
  updatedAt?: string;
}

async function submitIssueReport(payload: IssueReportPayload): Promise<IssueReportResponse> {
  const body = {
    category: payload.category,
    severity: payload.severity,
    description: payload.description,
    evidence: payload.evidence,
    screenshot: payload.screenshot,
    screenshot_mime_type: payload.screenshotMimeType ?? 'image/png',
    url: payload.url ?? window.location.href,
    user_agent: payload.userAgent ?? navigator.userAgent,
    session_id: payload.sessionId,
    correlation_id: payload.correlationId,
  };
  const resp = await apiClient.post<IssueReportApiResponse>(`${API_URL}/issue-reports`, body);
  return {
    id: resp.data.id,
    category: resp.data.category,
    severity: resp.data.severity,
    status: resp.data.status,
    description: resp.data.description,
    url: resp.data.url,
    sessionId: resp.data.session_id,
    correlationId: resp.data.correlation_id,
    githubIssueNumber: resp.data.github_issue_number,
    githubIssueUrl: resp.data.github_issue_url,
    createdAt: resp.data.created_at,
    updatedAt: resp.data.updated_at,
  };
}

// ── Report browsing (owner-scoped for non-admins) ───────────────────────

export interface IssueReportListItem {
  id: number;
  category: string;
  severity: string;
  status: string;
  description: string;
  url?: string;
  session_id?: string;
  correlation_id?: string;
  github_issue_number?: number;
  github_issue_url?: string;
  created_at: string;
  updated_at?: string;
}

export interface IssueReportDetail {
  id: number;
  user_id?: number;
  organization_id?: number;
  category: string;
  severity: string;
  status: string;
  description: string;
  evidence?: Record<string, unknown>;
  has_screenshot: boolean;
  screenshot_mime_type?: string;
  url?: string;
  user_agent?: string;
  session_id?: string;
  correlation_id?: string;
  github_issue_number?: number;
  github_issue_url?: string;
  created_at: string;
  updated_at?: string;
  resolved_at?: string;
}

export interface IssueReportListParams {
  category?: string;
  severity?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export interface IssueReportListResponse {
  items: IssueReportListItem[];
  total: number;
  limit: number;
  offset: number;
}

async function listIssueReports(params: IssueReportListParams = {}): Promise<IssueReportListResponse> {
  const queryParams: Record<string, string> = {};
  if (params.category) queryParams.category = params.category;
  if (params.severity) queryParams.severity = params.severity;
  if (params.status) queryParams.status = params.status;
  if (params.limit) queryParams.limit = String(params.limit);
  if (params.offset) queryParams.offset = String(params.offset);
  const resp = await apiClient.get<IssueReportListResponse>(`${API_URL}/issue-reports`, { params: queryParams });
  return resp.data;
}

async function getIssueReportDetail(reportId: number): Promise<IssueReportDetail> {
  const resp = await apiClient.get<IssueReportDetail>(`${API_URL}/issue-reports/${reportId}`);
  return resp.data;
}

async function retryGithubIssue(reportId: number): Promise<IssueReportApiResponse> {
  const resp = await apiClient.post<IssueReportApiResponse>(`${API_URL}/issue-reports/${reportId}/github`);
  return resp.data;
}

async function getIssueReportScreenshotUrl(reportId: number): Promise<string> {
  return `${API_URL}/issue-reports/${reportId}/screenshot`;
}

// ── Analytics ──────────────────────────────────────────────────────────

export interface IssueReportAnalytics {
  total: number;
  by_category: { label: string; count: number }[];
  by_severity: { label: string; count: number }[];
  by_status: { label: string; count: number }[];
  daily_trend: { date: string; count: number }[];
  github_linked: number;
  with_screenshot: number;
}

async function getIssueReportAnalytics(): Promise<IssueReportAnalytics> {
  const resp = await apiClient.get<IssueReportAnalytics>(`${API_URL}/issue-reports/analytics`);
  return resp.data;
}

export default {
  submitIssueReport,
  listIssueReports,
  getIssueReportDetail,
  retryGithubIssue,
  getIssueReportScreenshotUrl,
  getIssueReportAnalytics,
};
